import {
  defineStore,
  storeToRefs,
} from 'pinia';
import {
  computed,
  ref,
} from 'vue';

import { loadCollections } from '@/entities/collection/api';
import { useSentry } from '@/plugins/sentry';
import type { SentCollection } from '@/store/collection/types';
import { COLLECTION_TYPES } from '@/store/collection/types';
import { useDealStore } from '@/store/deal';
import type { Deal } from '@/types/Deal';

import type { Ref } from 'vue';

export const useDealCollectionsStore = defineStore('dealCollections', () => {
  const $sentry = useSentry();
  const dealStore = useDealStore();
  const { deal }: { deal: Ref<Deal> } = storeToRefs(dealStore);
  const collectionsByAgentList = ref<Array<SentCollection>>([]);
  const collectionsByAgentLoading = ref<boolean>(false);

  const collectionsByManagerList = ref<Array<SentCollection>>([]);
  const collectionsByManagerLoading = ref<boolean>(false);

  async function getCollectionsByManager () {
    try {
      collectionsByManagerLoading.value = true;

      const bookingId = deal?.value?.id;

      if (!bookingId) {
        throw new Error('bookingId is not defined');
      }
      collectionsByManagerList.value = await loadCollections({ bookingId }, COLLECTION_TYPES.manager).then((res) => res.result);
    } catch (error) {
      console.log('🚀 ~ file: dealCollections.ts ~ getCollectionsByManager ~ error', error);
      $sentry.captureException(error);
    } finally {
      collectionsByManagerLoading.value = false;
    }
  }

  async function getCollectionsByAgent () {
    try {
      collectionsByAgentLoading.value = true;

      const search = deal?.value?.user?.phone;

      if (!search) {
        throw new Error('search "phone" is not defined');
      }
      collectionsByAgentList.value = await loadCollections({ search }, COLLECTION_TYPES.sends).then((res) => res.result);
    } catch (error) {
      console.log('🚀 ~ file: dealCollections.ts ~ getCollectionsByAgent ~ error', error);
      $sentry.captureException(error);
    } finally {
      collectionsByAgentLoading.value = false;
    }
  }

  async function getLists () {
    await getCollectionsByAgent();
    await getCollectionsByManager();
  }

  return {
    collectionsByAgentList,
    collectionsByManagerList,
    getLists,
    anyLoading: computed(() => collectionsByAgentLoading.value || collectionsByManagerLoading.value),
  };
});
